import { QBankStructureLevelCategory } from '@exam-management/graphql';
import {
  Alert,
  ArrowBackIosRoundedIcon,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Text,
} from '@shared/components';
import { useFormCheckbox } from '@shared/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { SubTopicSelection, TopicSelection } from '../../components';
import { type BaseQuestionConditionsContextType } from '../../types';

export interface SelectTopicContentProps
  extends Pick<
    BaseQuestionConditionsContextType,
    'chapterData' | 'levelItemsWithLevel' | 'checkExistOnStructures'
  > {
  mode: 'create' | 'edit';
  onClose: () => void;
  onBack: () => void;
}

export default function SelectTopicContent(props: Readonly<SelectTopicContentProps>) {
  const { mode, chapterData, levelItemsWithLevel, checkExistOnStructures, onClose, onBack } = props;

  const formContext = useFormContext();

  const [previousValue, setPreviousValue] = useState({});
  const formSubtopics = formContext.watch('location.subtopics');
  const isEditedMode = mode === 'edit';

  const handleApplySelectTopic = () => {
    formContext.setValue('questionType', {
      calculated: {
        easy: null,
        medium: null,
        hard: null,
      },
      notCalculated: {
        easy: null,
        medium: null,
        hard: null,
      },
    });
    onBack();
  };

  const subtopics = useMemo(() => {
    return levelItemsWithLevel
      .filter((item) => item.level?.category === QBankStructureLevelCategory.SubTopic)
      ?.sort((a, b) => a.order - b.order);
  }, [levelItemsWithLevel]);

  const topics = useMemo(() => {
    const topicLevelItems = levelItemsWithLevel
      .filter((item) => item.level?.category === QBankStructureLevelCategory.Topic)
      ?.sort((a, b) => a.order - b.order);

    if (!topicLevelItems.length) {
      //TODO: make sure that the chapter id should be removed from topics before submitting to BE
      return [
        {
          id: chapterData?.refId ?? '',
          tag: {
            name: 'หัวข้อ: ไม่เปิดใช้งาน',
          },
        },
      ];
    }

    return topicLevelItems;
  }, [levelItemsWithLevel, subtopics]);

  const checkBoxLevelItems = useMemo(() => {
    return topics.map(({ id }) => {
      const children = subtopics
        .filter((subTopic) => subTopic.parentLevelItemId === id)
        .map((subTopic) => {
          return {
            name: `subtopics.${subTopic.id}`,
          };
        });

      return {
        name: `topics.${id}`,
        children,
      };
    });
  }, [topics, subtopics]);

  const {
    getCheckboxState,
    updateCheckboxState,
    selectAll: { state: selectAllState, change },
  } = useFormCheckbox({
    data: checkBoxLevelItems,
    options: {
      prefix: 'location',
    },
  });

  const isAllSubtopicsSelected = useMemo(() => {
    return (
      subtopics.length > 0 && subtopics.every((subTopic) => checkExistOnStructures(subTopic.id))
    );
  }, [subtopics]);

  const isAnySubtopicsSelected = useMemo(() => {
    return Object.values(formContext.watch('location.subtopics') ?? {}).some(
      (value) => value === true,
    );
  }, [formContext]);

  const isSameAsPreviousValue = useMemo(() => {
    const location = formContext.getValues(['location'])[0];
    return JSON.stringify(location) === JSON.stringify(previousValue);
  }, [formContext, previousValue]);

  const handleDiscardAndBack = () => {
    if (Object.keys(previousValue).length > 0) {
      formContext.setValue(
        'location',
        previousValue as {
          topics: Record<string, boolean>;
          subtopics: Record<string, boolean>;
        },
      );
    }
    onBack();
  };

  useEffect(() => {
    if (Object.keys(previousValue).length === 0) {
      const location = formContext.getValues(['location'])[0];
      setPreviousValue({ topics: { ...location?.topics }, subtopics: { ...location?.subtopics } });
    }

    return () => {
      setPreviousValue({});
    };
  }, []);

  return (
    <>
      <DialogTitle onClose={onClose}>
        <Flex sx={{ alignItems: 'center' }}>
          <ArrowBackIosRoundedIcon
            onClick={handleDiscardAndBack}
            sx={(theme) => ({
              mr: theme.spacing(4),
              ml: theme.spacing(-1),
              fontSize: 24,
              cursor: 'pointer',
            })}
          />
          Select Topic
        </Flex>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          height: '600px',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: (theme) => theme.spacing(4),
          }}
        >
          {/* TODO: replace the following Flex with InfoSection */}
          <Flex
            sx={(theme) => ({
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: theme.shape.rounded(2),
              border: '1px solid',
              borderColor: theme.palette.grey[200],
              bgcolor: theme.palette.grey[50],
              p: theme.spacing(4),
            })}
          >
            <Text variant='body1Semibold'>{chapterData?.name}</Text>
            <Chip
              size='small'
              label={`QSource: ${chapterData?.refCode ?? '-'}`}
              sx={(theme) => ({
                bgcolor: theme.palette.grey[200],
                border: '1px solid',
                borderColor: theme.palette.grey[300],
              })}
            />
          </Flex>
          {isAllSubtopicsSelected && !isEditedMode ? (
            <Alert
              severity='info'
              sx={(theme) => ({
                bgcolor: theme.palette.grey[100],
                color: theme.palette.grey[600],
                '&& .MuiSvgIcon-root': {
                  color: theme.palette.grey[600],
                },
              })}
            >
              ไม่สามารถเลือกหัวข้อได้อีก เนื่องจากหัวข้อทั้งหมดถูกเลือกไปหมดแล้ว
            </Alert>
          ) : null}
          <Flex sx={{ alignItems: 'center' }}>
            <Text variant='h5' sx={{ flex: 1 }}>
              เลือกหัวข้อที่ต้องการ
            </Text>
            <Checkbox
              label='Select All'
              checked={selectAllState.checked}
              indeterminate={selectAllState.indeterminate}
              disabled={isAllSubtopicsSelected && !isEditedMode}
              onChange={(e) => {
                change(e.target.checked);
              }}
              sx={(theme) => ({
                p: theme.spacing(0),
                color: theme.palette.grey[400],
              })}
              slotProps={{
                formControlLabel: {
                  sx: (theme) => ({
                    gap: theme.spacing(2),
                  }),
                },
              }}
            />
          </Flex>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(4),
            })}
          >
            {topics.map((data) => {
              const { id, tag } = data;
              const name = tag?.name ?? 'หัวข้อ: ไม่ระบุ';
              const subtopicsByTopic = subtopics.filter((item) => item.parentLevelItemId === id);

              const selectedChildrenCount = subtopicsByTopic.filter((subTopic) => {
                const isSelected = formSubtopics?.[subTopic.id] === true;
                const isExisting = checkExistOnStructures(subTopic.id);
                return isSelected || isExisting;
              }).length;
              const childrenCount = subtopicsByTopic.length;

              const isSubtopicsExistInStructures = subtopicsByTopic.every((subTopic) =>
                checkExistOnStructures(subTopic.id),
              );

              const checkBoxState = getCheckboxState(`topics.${id}`);

              if (childrenCount === 0) return null;
              const isIndeterminate = checkBoxState.indeterminate;
              const isTopicChecked = checkBoxState.checked || isSubtopicsExistInStructures;
              const isTopicDisabled = isSubtopicsExistInStructures && !isEditedMode;

              return (
                <TopicSelection
                  key={id}
                  name={`location.topics.${id}`}
                  label={name}
                  indeterminate={isIndeterminate}
                  defaultChecked={isTopicDisabled ? isSubtopicsExistInStructures : undefined}
                  checked={isTopicChecked}
                  endAdornment={
                    <Text variant='body2' color='textSecondary'>
                      {selectedChildrenCount}/{childrenCount} หัวข้อย่อย
                    </Text>
                  }
                  disabled={isTopicDisabled}
                  onChange={(e) => {
                    updateCheckboxState(`topics.${id}`, e.target.checked);
                  }}
                >
                  <Divider />
                  <Flex
                    sx={(theme) => ({
                      flexDirection: 'column',
                      pl: theme.spacing(8),
                    })}
                  >
                    {subtopicsByTopic.map((subTopic) => {
                      const { id } = subTopic;
                      const name = subTopic.tag?.name ?? 'หัวข้อย่อย: ไม่ระบุ';

                      const isAlreadyExistOnStructures = checkExistOnStructures(id);
                      const subtopicCheckboxState = getCheckboxState(`subtopics.${id}`);
                      const isSubtopicChecked =
                        subtopicCheckboxState.checked || isAlreadyExistOnStructures;
                      const isSubtopicDisabled = isAlreadyExistOnStructures && !isEditedMode;

                      return (
                        <SubTopicSelection
                          key={id}
                          name={`location.subtopics.${id}`}
                          label={name}
                          checked={isSubtopicChecked}
                          defaultChecked={
                            isSubtopicDisabled ? isAlreadyExistOnStructures : undefined
                          }
                          disabled={isSubtopicDisabled}
                          onChange={(e) => {
                            updateCheckboxState(`subtopics.${subTopic.id}`, e.target.checked);
                          }}
                        />
                      );
                    })}
                  </Flex>
                </TopicSelection>
              );
            })}
          </Flex>
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='secondary' size='large'>
          Cancel
        </Button>
        <Button
          size='large'
          onClick={handleApplySelectTopic}
          disabled={
            (isAllSubtopicsSelected && !isEditedMode) ||
            !isAnySubtopicsSelected ||
            isSameAsPreviousValue
          }
        >
          Apply
        </Button>
      </DialogActions>
    </>
  );
}
